import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../lib/use-auth.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { reconciliationReportsRepoFor } from '../lib/repositories/reconciliation-reports-repo.js';
import { settlementReportsRepoFor } from '../lib/repositories/settlement-reports-repo.js';
import {
  callReconcilePaymentsManual,
  callUploadSettlementCsv,
} from '../lib/callables/reconciliation.js';
import { callMarkInvoicePaidManually } from '../lib/callables/billing.js';
import { logger } from '../lib/logger.js';
import { PAGE_FRAME_MAX, dashboardPageStyle, sectionGridStyle } from '../lib/layout.js';
import {
  celebrateOnce,
  eyebrowStyle,
  ghostLinkStyle,
  haptic,
  pageStyle,
  primaryButtonStyle,
} from '@kilat/ui';
import {
  money,
  type ReconciliationMismatch,
  type ReconciliationMismatchReason,
  type ReconciliationReport,
  type SettlementMismatch,
  type SettlementMismatchReason,
  type SettlementReport,
} from '@kilat/shared';

/**
 * /superadmin/payments — Sprint 100.
 *
 * Daily payment reconciliation dashboard. Surfaces:
 *   - Last 30 days of reconciliation reports (written by `reconcilePaymentsDaily`
 *     at 03:00 SGT each day).
 *   - Per-day status pill (✓ clean | ⚠ N mismatches), counts, totals,
 *     drift between operator-stated amounts and actually-credited amounts.
 *   - Drill-down into each report's `mismatches[]` so super-admin can
 *     manually investigate (write adjustment ledger entries, re-issue
 *     missing wash packs, etc.).
 *   - "Run reconciliation now" button for the current day (before the
 *     cron fires) or to re-run an existing report.
 *
 * Brand-scoped: super-admin sees ONLY reports for their brand (ADR-0017).
 * Per [no-dead-ends] every state explicit: loading / empty / error /
 * running / done / failed.
 */

export function SuperadminPaymentsPage() {
  const { claims, loading: authLoading } = useAuth();
  const brand = usePlatformBrand();
  const isSuper = useMemo(() => isSuperAdminForBrand(claims, brand.id), [claims, brand.id]);

  const subscribe = useMemo(() => {
    if (!isSuper) return null;
    const repo = reconciliationReportsRepoFor(brand.id);
    return (cb: (vs: ReconciliationReport[]) => void, onError?: (err: Error) => void) =>
      repo.listSnapshot(cb, onError);
  }, [isSuper, brand.id]);
  const {
    data: reports,
    loading: reportsLoading,
    error: reportsError,
  } = useTrackedSnapshot<ReconciliationReport[]>(['reconciliation-reports', brand.id], subscribe);

  const subscribeSettlement = useMemo(() => {
    if (!isSuper) return null;
    const repo = settlementReportsRepoFor(brand.id);
    return (cb: (vs: SettlementReport[]) => void, onError?: (err: Error) => void) =>
      repo.listSnapshot(cb, onError);
  }, [isSuper, brand.id]);
  const {
    data: settlementReports,
    loading: settlementLoading,
    error: settlementError,
  } = useTrackedSnapshot<SettlementReport[]>(['settlement-reports', brand.id], subscribeSettlement);

  // Sort newest first — report id is YYYY-MM-DD so string sort works.
  const sorted = useMemo(
    () => (reports ?? []).slice().sort((a, b) => (a.id < b.id ? 1 : -1)),
    [reports],
  );

  // Settlement reports use `ts-{uploadMs}` ids which also string-sort newest-first
  // when reversed. Cap at the most recent 10 — older uploads are still readable
  // via Firestore directly if super-admin needs to dig deeper.
  const sortedSettlement = useMemo(
    () =>
      (settlementReports ?? [])
        .slice()
        .sort((a, b) => (a.id < b.id ? 1 : -1))
        .slice(0, 10),
    [settlementReports],
  );

  if (authLoading) return <Screen title="Loading…" />;
  if (!isSuper) {
    return (
      <Screen
        title="Super-admin only"
        body={`/superadmin/payments is restricted to ${brand.name} super-admins. Sign in with a super-admin account, or head back to the queue.`}
        cta={{ label: 'Back to queue', to: '/queue' }}
      />
    );
  }

  return (
    <main style={dashboardPageStyle}>
      <header style={headerStyle}>
        <div style={{ maxWidth: PAGE_FRAME_MAX, margin: '0 auto', padding: 'var(--space-5)' }}>
          <p style={eyebrowStyle}>Super-admin · Payments</p>
          <h1
            style={{ margin: 'var(--space-1) 0 0 0', fontSize: 'var(--text-h1)', fontWeight: 600 }}
          >
            Payment reconciliation
          </h1>
          <p
            style={{
              margin: 'var(--space-2) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              maxWidth: 720,
            }}
          >
            One report per day per brand. Compares paid payment intents against the operator wallet
            credits we wrote in response. ✓ clean means every paid intent landed correctly; ⚠ means
            one or more mismatches need investigation.
          </p>
          <div
            style={{
              marginTop: 'var(--space-4)',
              display: 'flex',
              gap: 'var(--space-3)',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Link to="/queue" style={ghostLinkStyle}>
              ← Back to queue
            </Link>
            <ManualReconcileButton brandId={brand.id} />
          </div>
        </div>
      </header>

      <section style={contentStyle}>
        {/* The two manual tools side by side on a wide screen. */}
        <div style={sectionGridStyle}>
          <MarkInvoicePaidSection brandId={brand.id} />

          <SettlementCsvSection
            brandId={brand.id}
            reports={sortedSettlement}
            loading={settlementLoading || settlementReports === null}
            error={settlementError}
          />
        </div>

        <h2 style={sectionHeadingStyle}>Daily reconciliation</h2>
        {reportsError ? (
          <InlineNote tone="error">
            Couldn't load reports: {String(reportsError.message ?? reportsError)}.
          </InlineNote>
        ) : reportsLoading || reports === null ? (
          <SkeletonRow />
        ) : sorted.length === 0 ? (
          <InlineNote>
            No reports yet. The first one will land at 03:00 SGT after the platform has its first
            paid customer. Or click "Run reconciliation now" above to generate today's report on
            demand.
          </InlineNote>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
            {sorted.map((r) => (
              <ReportRow key={r.id} report={r} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

// ── ADR-0022 Option C — manual invoice mark-paid ───────────────────────

type MarkMethod = 'manual_bank_transfer' | 'cash' | 'waived';

const METHOD_OPTIONS: readonly { readonly id: MarkMethod; readonly label: string }[] = [
  { id: 'manual_bank_transfer', label: 'Bank transfer / DuitNow' },
  { id: 'cash', label: 'Cash' },
  { id: 'waived', label: 'Waive (write off)' },
];

/**
 * Manual invoice payment. Until a live operator-subscription gateway is
 * wired (ADR-0022), operators pay by bank transfer / DuitNow and a
 * super-admin records it here. Writes the same `open|past_due → paid`
 * invoice transition the automated rail would, so commission accrual +
 * the tenant lifecycle sweeper fire unchanged.
 *
 * Per [no-dead-ends] every state is explicit: form → confirm → submitting
 * → done (with next action) / failed (with a friendly reason + retry).
 */
function MarkInvoicePaidSection({ brandId }: { readonly brandId: string }) {
  const [tenantId, setTenantId] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [method, setMethod] = useState<MarkMethod>('manual_bank_transfer');
  const [reference, setReference] = useState('');
  const [state, setState] = useState<
    | { kind: 'form' }
    | { kind: 'confirm' }
    | { kind: 'submitting' }
    | { kind: 'done'; alreadyPaid: boolean; totalSen: number; tenantId: string; invoiceId: string }
    | { kind: 'failed'; message: string }
  >({ kind: 'form' });

  const canSubmit = tenantId.trim().length > 0 && invoiceId.trim().length > 0;
  const methodLabel = METHOD_OPTIONS.find((m) => m.id === method)?.label ?? method;

  async function submit() {
    setState({ kind: 'submitting' });
    haptic.tap();
    const tid = tenantId.trim();
    const iid = invoiceId.trim();
    try {
      const result = await callMarkInvoicePaidManually({
        brandId,
        tenantId: tid,
        invoiceId: iid,
        method,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
      });
      setState({
        kind: 'done',
        alreadyPaid: result.alreadyPaid,
        totalSen: result.totalSen,
        tenantId: tid,
        invoiceId: iid,
      });
      logger.info('invoice marked paid manually', {
        brandId,
        tenantId: tid,
        invoiceId: iid,
        ...result,
      });
      void celebrateOnce({ key: `invoice-paid:${tid}:${iid}`, intensity: 'micro' }).then(
        (fired) => {
          if (fired) haptic.success();
        },
      );
    } catch (e) {
      logger.error('mark invoice paid failed', {
        brandId,
        tenantId: tid,
        invoiceId: iid,
        error: String(e),
      });
      setState({ kind: 'failed', message: extractMarkPaidError(e) });
    }
  }

  function resetForm() {
    setTenantId('');
    setInvoiceId('');
    setReference('');
    setMethod('manual_bank_transfer');
    setState({ kind: 'form' });
  }

  if (state.kind === 'done') {
    return (
      <div style={{ display: 'grid', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
        <h2 style={sectionHeadingStyle}>Mark an invoice paid</h2>
        <div
          role="status"
          style={{
            ...uploadCardStyle,
            background: 'rgba(13,122,79,0.06)',
            border: '1px solid rgba(13,122,79,0.22)',
          }}
        >
          <p style={{ margin: 0, fontWeight: 600, color: 'var(--color-primary)' }}>
            {state.alreadyPaid
              ? `Invoice ${state.invoiceId} was already paid — nothing changed.`
              : `Invoice ${state.invoiceId} recorded as paid · ${money.format(state.totalSen)}.`}
          </p>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {state.alreadyPaid
              ? 'This action is idempotent, so re-recording is safe.'
              : 'Commission accrual fires from the invoice trigger; the tenant returns to active on the next daily lifecycle sweep (or run it now below).'}
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <button type="button" onClick={resetForm} style={primaryButtonStyle}>
              Mark another invoice
            </button>
            <Link to="/superadmin/tenants" style={ghostLinkStyle}>
              View operators →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
      <h2 style={sectionHeadingStyle}>Mark an invoice paid</h2>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          maxWidth: 720,
        }}
      >
        Record an operator SaaS-subscription payment received out-of-band (bank transfer / DuitNow /
        cash), or waive a balance. This is the launch collection path until an automated
        operator-billing gateway is live. It flips the invoice to paid — the same transition the
        automated rail uses — so agent commission and tenant reactivation happen exactly as they
        would automatically.
      </p>

      <div style={{ ...uploadCardStyle, gap: 'var(--space-4)' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
            gap: 'var(--space-3)',
          }}
        >
          <MarkField label="Tenant ID">
            <input
              value={tenantId}
              onChange={(e) => {
                setTenantId(e.target.value);
                setState({ kind: 'form' });
              }}
              placeholder="e.g. sparkle-carwash-x7a2"
              style={markInputStyle}
              autoComplete="off"
            />
          </MarkField>
          <MarkField label="Invoice ID">
            <input
              value={invoiceId}
              onChange={(e) => {
                setInvoiceId(e.target.value);
                setState({ kind: 'form' });
              }}
              placeholder="e.g. 2026-07"
              style={markInputStyle}
              autoComplete="off"
            />
          </MarkField>
        </div>

        <MarkField label="Method">
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {METHOD_OPTIONS.map((opt) => {
              const active = method === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setMethod(opt.id)}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 'var(--radius-pill)',
                    border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border-strong)'}`,
                    background: active ? 'var(--color-primary)' : 'transparent',
                    color: active ? 'var(--color-primary-fg)' : 'var(--color-on-surface)',
                    fontSize: 'var(--text-meta)',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </MarkField>

        <MarkField label="Reference / note (optional)">
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Bank ref, receipt no., or why it was waived"
            maxLength={500}
            style={markInputStyle}
            autoComplete="off"
          />
        </MarkField>

        {state.kind === 'confirm' ? (
          <div
            style={{
              display: 'grid',
              gap: 'var(--space-3)',
              padding: 'var(--space-4)',
              borderRadius: 'var(--radius-md)',
              background: 'rgba(245,158,11,0.06)',
              border: '1px solid rgba(245,158,11,0.22)',
            }}
          >
            <p
              style={{ margin: 0, fontSize: 'var(--text-meta)', color: '#92400e', lineHeight: 1.5 }}
            >
              Record <strong>{invoiceId.trim()}</strong> for tenant{' '}
              <strong>{tenantId.trim()}</strong> as paid via <strong>{methodLabel}</strong>? Agent
              commission may accrue immediately. To reverse, you'd refund/void the invoice.
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <button type="button" onClick={() => void submit()} style={primaryButtonStyle}>
                Yes, mark paid
              </button>
              <button
                type="button"
                onClick={() => setState({ kind: 'form' })}
                style={{
                  ...primaryButtonStyle,
                  background: 'transparent',
                  color: 'var(--color-on-surface)',
                  border: '1px solid var(--color-border-strong)',
                  boxShadow: 'none',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-3)',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={() => setState({ kind: 'confirm' })}
              disabled={!canSubmit || state.kind === 'submitting'}
              style={{
                ...primaryButtonStyle,
                opacity: !canSubmit || state.kind === 'submitting' ? 0.5 : 1,
                cursor: !canSubmit
                  ? 'not-allowed'
                  : state.kind === 'submitting'
                    ? 'wait'
                    : 'pointer',
              }}
            >
              {state.kind === 'submitting' ? 'Recording payment…' : 'Mark invoice paid'}
            </button>
            {!canSubmit ? (
              <span
                style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}
              >
                Enter a tenant ID and invoice ID to continue.
              </span>
            ) : null}
          </div>
        )}

        {state.kind === 'failed' ? (
          <p
            role="alert"
            style={{ margin: 0, fontSize: 'var(--text-meta)', color: 'var(--color-accent)' }}
          >
            {state.message}{' '}
            <button
              type="button"
              onClick={() => setState({ kind: 'form' })}
              style={dismissBtnStyle}
            >
              dismiss
            </button>
          </p>
        ) : null}
      </div>
    </div>
  );
}

function MarkField({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <span
        style={{ fontSize: 'var(--text-meta)', fontWeight: 500, color: 'var(--color-on-surface)' }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function extractMarkPaidError(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const m = String((err as { message: string }).message);
    if (m.includes('permission-denied')) {
      return "You don't have permission to mark invoices paid for this brand, or the tenant belongs to another brand.";
    }
    if (m.includes('not-found')) {
      return 'No invoice with that ID exists for that tenant. Double-check both IDs.';
    }
    if (m.includes('failed-precondition')) {
      return 'That invoice is voided or refunded — it can’t be marked paid. Only open or past-due invoices can.';
    }
    if (m.includes('invalid-argument')) {
      return 'One of the fields is invalid. Check the tenant ID, invoice ID, and method.';
    }
    return m;
  }
  return 'Something went wrong recording the payment. Try again or check the function logs.';
}

const markInputStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-border-strong)',
  fontSize: 'var(--text-body)',
  fontFamily: 'inherit',
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
};

// ── Sprint 120 — Settlement CSV upload + recent uploads ────────────────

function SettlementCsvSection({
  brandId,
  reports,
  loading,
  error,
}: {
  readonly brandId: string;
  readonly reports: readonly SettlementReport[];
  readonly loading: boolean;
  readonly error: Error | null;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
      <h2 style={sectionHeadingStyle}>Settlement statement cross-check</h2>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          maxWidth: 720,
        }}
      >
        Upload a per-bill CSV exported from your toyyibPay dashboard. Each row is cross-checked
        against our paid payment intents to confirm the PSP actually has the money on record. ✓
        clean means every CSV row matched a paid intent at the same amount, AND every paid intent in
        the window appears in the CSV.
      </p>
      <SettlementUploadCard brandId={brandId} />
      {error ? (
        <InlineNote tone="error">
          Couldn't load settlement reports: {String(error.message ?? error)}.
        </InlineNote>
      ) : loading ? (
        <SkeletonRow />
      ) : reports.length === 0 ? (
        <InlineNote>
          No settlement uploads yet. After your first upload the most recent 10 land here.
        </InlineNote>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          {reports.map((r) => (
            <SettlementReportRow key={r.id} report={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function SettlementUploadCard({ brandId }: { readonly brandId: string }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'reading'; fileName: string }
    | { kind: 'uploading'; fileName: string }
    | {
        kind: 'done';
        reportId: string;
        clean: boolean;
        rowsParsed: number;
        rowsMatched: number;
        intentsMissingFromCsv: number;
        mismatchesCount: number;
      }
    | { kind: 'failed'; message: string }
  >({ kind: 'idle' });

  async function onFile(file: File) {
    setState({ kind: 'reading', fileName: file.name });
    let text: string;
    try {
      text = await file.text();
    } catch (e) {
      setState({ kind: 'failed', message: `Couldn't read the file: ${String(e)}` });
      return;
    }
    if (text.length === 0) {
      setState({ kind: 'failed', message: 'The file is empty.' });
      return;
    }
    if (text.length > 5 * 1024 * 1024) {
      setState({
        kind: 'failed',
        message: `File is ${(text.length / 1024 / 1024).toFixed(1)} MB — limit is 5 MB. Split the export by month + upload one at a time.`,
      });
      return;
    }
    setState({ kind: 'uploading', fileName: file.name });
    try {
      const result = await callUploadSettlementCsv({
        brandId,
        source: 'toyyibpay',
        csvText: text,
        fileName: file.name,
      });
      setState({
        kind: 'done',
        reportId: result.reportId,
        clean: result.clean,
        rowsParsed: result.rowsParsed,
        rowsMatched: result.rowsMatched,
        intentsMissingFromCsv: result.intentsMissingFromCsv,
        mismatchesCount: result.mismatchesCount,
      });
      logger.info('settlement upload complete', { brandId, ...result });
    } catch (e) {
      logger.error('settlement upload failed', { brandId, error: String(e) });
      setState({ kind: 'failed', message: extractSettlementError(e) });
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div style={uploadCardStyle}>
      <div
        style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}
      >
        <label style={primaryButtonStyle}>
          {state.kind === 'reading' || state.kind === 'uploading'
            ? `Working on ${state.fileName}…`
            : 'Upload settlement CSV'}
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            disabled={state.kind === 'reading' || state.kind === 'uploading'}
            onChange={(ev) => {
              const f = ev.target.files?.[0];
              if (f) void onFile(f);
            }}
            style={{ display: 'none' }}
          />
        </label>
        <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
          Per-bill export from toyyibPay (Sales Report → CSV)
        </span>
      </div>
      {state.kind === 'done' ? (
        <p
          role="status"
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: state.clean ? 'var(--color-primary)' : '#b45309',
            fontWeight: 500,
          }}
        >
          {state.clean
            ? `✓ ${state.reportId} clean · ${state.rowsMatched}/${state.rowsParsed} rows matched`
            : `⚠ ${state.reportId} · ${state.mismatchesCount} mismatch${state.mismatchesCount === 1 ? '' : 'es'} (${state.rowsMatched}/${state.rowsParsed} rows matched, ${state.intentsMissingFromCsv} intent${state.intentsMissingFromCsv === 1 ? '' : 's'} missing from CSV)`}{' '}
          <button type="button" onClick={() => setState({ kind: 'idle' })} style={dismissBtnStyle}>
            dismiss
          </button>
        </p>
      ) : state.kind === 'failed' ? (
        <p
          role="alert"
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-accent)',
          }}
        >
          {state.message}{' '}
          <button type="button" onClick={() => setState({ kind: 'idle' })} style={dismissBtnStyle}>
            dismiss
          </button>
        </p>
      ) : null}
    </div>
  );
}

function SettlementReportRow({ report }: { readonly report: SettlementReport }) {
  const drift = report.totalCsvAmountSen - report.totalMatchedIntentSen;
  return (
    <article style={reportCardStyle}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
            {report.fileName}
          </h3>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            Uploaded {formatTimeFromTs(report.uploadedAt)}
            {' · '}
            {report.uploadedBy.slice(0, 8)}…{' · '}
            {(report.fileSizeBytes / 1024).toFixed(1)} KB
          </p>
        </div>
        <CleanPill clean={report.clean} mismatchCount={report.mismatches.length} />
      </div>

      <div style={statGridStyle}>
        <Stat label="Rows total" value={String(report.rowsTotal)} />
        <Stat label="Parsed" value={String(report.rowsParsed)} />
        <Stat
          label="Matched"
          value={`${report.rowsMatched} / ${report.rowsParsed}`}
          hint="Same amount + paid intent exists"
        />
        <Stat
          label="Missing intents"
          value={String(report.intentsMissingFromCsv)}
          hint="Paid intents not in this CSV"
        />
        <Stat label="CSV total" value={money.format(report.totalCsvAmountSen)} />
        <Stat label="Matched intents" value={money.format(report.totalMatchedIntentSen)} />
        <Stat
          label="Drift"
          value={
            drift === 0
              ? '—'
              : drift > 0
                ? `+${money.format(drift)}`
                : `−${money.format(Math.abs(drift))}`
          }
          hint="CSV − matched intents"
        />
        <Stat
          label="Period"
          value={
            report.periodStart && report.periodEnd
              ? `${formatDateFromTs(report.periodStart)} → ${formatDateFromTs(report.periodEnd)}`
              : '—'
          }
        />
      </div>

      {report.detectedHeaders.length > 0 ? (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          Headers:{' '}
          <code style={{ fontFamily: 'ui-monospace, monospace' }}>
            {report.detectedHeaders.join(' · ')}
          </code>
        </p>
      ) : null}

      {report.mismatches.length > 0 ? (
        <SettlementMismatchList mismatches={report.mismatches} />
      ) : null}
    </article>
  );
}

function SettlementMismatchList({
  mismatches,
}: {
  readonly mismatches: readonly SettlementMismatch[];
}) {
  return (
    <details style={mismatchBoxStyle}>
      <summary
        style={{
          cursor: 'pointer',
          fontSize: 'var(--text-meta)',
          fontWeight: 500,
          color: '#b45309',
        }}
      >
        View {mismatches.length} mismatch{mismatches.length === 1 ? '' : 'es'}
      </summary>
      <ul
        style={{
          listStyle: 'none',
          margin: 'var(--space-3) 0 0 0',
          padding: 0,
          display: 'grid',
          gap: 'var(--space-2)',
        }}
      >
        {mismatches.map((m, i) => (
          <li key={`${m.externalRef ?? 'noref'}-${i}`} style={mismatchItemStyle}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 'var(--space-3)',
                alignItems: 'baseline',
                flexWrap: 'wrap',
              }}
            >
              <strong style={{ fontSize: 'var(--text-meta)', color: '#991b1b' }}>
                {settlementReasonLabel(m.reason)}
                {m.rowIndex !== undefined ? ` · row ${m.rowIndex}` : ''}
              </strong>
              <span
                style={{
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                  fontFamily: 'ui-monospace, monospace',
                }}
              >
                {m.externalRef ?? '—'}
              </span>
            </div>
            <p
              style={{
                margin: 'var(--space-1) 0 0 0',
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface)',
              }}
            >
              {m.detail}
            </p>
            {m.csvAmountSen !== undefined || m.intentAmountSen !== undefined ? (
              <p
                style={{
                  margin: 'var(--space-1) 0 0 0',
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                }}
              >
                {m.csvAmountSen !== undefined ? `CSV ${money.format(m.csvAmountSen)}` : ''}
                {m.csvAmountSen !== undefined && m.intentAmountSen !== undefined ? ' · ' : ''}
                {m.intentAmountSen !== undefined ? `Intent ${money.format(m.intentAmountSen)}` : ''}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </details>
  );
}

function settlementReasonLabel(reason: SettlementMismatchReason): string {
  switch (reason) {
    case 'unparseable_row':
      return 'CSV row unparseable';
    case 'unknown_external_ref':
      return 'CSV row · no matching intent';
    case 'intent_not_paid':
      return 'CSV paid · intent not marked paid';
    case 'amount_mismatch':
      return 'Amount mismatch · CSV vs intent';
    case 'paid_intent_not_in_settlement':
      return 'Paid intent · missing from CSV';
  }
}

function extractSettlementError(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const m = String((err as { message: string }).message);
    if (m.includes('permission-denied')) {
      return "You don't have permission to upload settlement CSVs for this brand.";
    }
    if (m.includes('invalid-argument')) {
      return 'The CSV failed validation. Check it has at least an external-reference column and an amount column.';
    }
    return m;
  }
  return 'Upload failed. Try again or check the function logs.';
}

function formatDateFromTs(ts: { toMillis?: () => number; seconds?: number } | undefined): string {
  if (!ts) return '—';
  const ms =
    typeof ts.toMillis === 'function'
      ? ts.toMillis()
      : typeof ts.seconds === 'number'
        ? ts.seconds * 1000
        : 0;
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ── Manual reconcile button ────────────────────────────────────────────

function ManualReconcileButton({ brandId }: { readonly brandId: string }) {
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'running' }
    | { kind: 'done'; reportId: string; clean: boolean; mismatchesCount: number }
    | { kind: 'failed'; message: string }
  >({ kind: 'idle' });

  async function run() {
    setState({ kind: 'running' });
    try {
      const result = await callReconcilePaymentsManual({ brandId });
      setState({
        kind: 'done',
        reportId: result.reportId,
        clean: result.clean,
        mismatchesCount: result.mismatchesCount,
      });
      logger.info('manual reconciliation complete', { brandId, ...result });
    } catch (e) {
      logger.error('manual reconciliation failed', { brandId, error: String(e) });
      setState({
        kind: 'failed',
        message: extractFriendlyError(e),
      });
    }
  }

  return (
    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
      <button
        type="button"
        onClick={() => void run()}
        disabled={state.kind === 'running'}
        style={{
          ...primaryButtonStyle,
          opacity: state.kind === 'running' ? 0.6 : 1,
          cursor: state.kind === 'running' ? 'wait' : 'pointer',
        }}
      >
        {state.kind === 'running' ? 'Running reconciliation…' : 'Run reconciliation now'}
      </button>
      {state.kind === 'done' ? (
        <span
          role="status"
          style={{
            fontSize: 'var(--text-meta)',
            color: state.clean ? 'var(--color-primary)' : '#b45309',
            fontWeight: 500,
          }}
        >
          {state.clean
            ? `✓ ${state.reportId} clean`
            : `⚠ ${state.reportId} has ${state.mismatchesCount} mismatch${
                state.mismatchesCount === 1 ? '' : 'es'
              }`}{' '}
          <button
            type="button"
            onClick={() => setState({ kind: 'idle' })}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            dismiss
          </button>
        </span>
      ) : state.kind === 'failed' ? (
        <span role="alert" style={{ fontSize: 'var(--text-meta)', color: 'var(--color-accent)' }}>
          {state.message}{' '}
          <button
            type="button"
            onClick={() => setState({ kind: 'idle' })}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            dismiss
          </button>
        </span>
      ) : null}
    </div>
  );
}

// ── Report row + drilldown ──────────────────────────────────────────────

function ReportRow({ report }: { readonly report: ReconciliationReport }) {
  const drift = report.totalAmountSen - report.totalCreditedSen;
  const driftLabel =
    drift === 0
      ? '—'
      : drift > 0
        ? `−${money.format(drift)} (operators under-credited)`
        : `+${money.format(Math.abs(drift))} (operators over-credited)`;
  return (
    <article style={reportCardStyle}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>{report.id}</h2>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            Computed {formatTimeFromTs(report.computedAt)}
            {report.computedBy !== 'system'
              ? ` · manually by ${report.computedBy.slice(0, 8)}…`
              : ''}
          </p>
        </div>
        <CleanPill clean={report.clean} mismatchCount={report.mismatches.length} />
      </div>

      <div style={statGridStyle}>
        <Stat label="Paid intents" value={String(report.paidIntentsCount)} />
        <Stat label="Failed" value={String(report.failedIntentsCount)} />
        <Stat label="Still pending" value={String(report.pendingIntentsCount)} />
        <Stat
          label="Customers paid"
          value={money.format(report.totalChargedSen)}
          hint="Including the visible PSP fee"
        />
        <Stat
          label="Operator credits"
          value={money.format(report.totalAmountSen)}
          hint="What operators should receive"
        />
        <Stat
          label="Actually credited"
          value={money.format(report.totalCreditedSen)}
          hint="Sum of `collected` ledger entries"
        />
        <Stat label="Drift" value={driftLabel} />
        <Stat
          label="Estimated PSP fees"
          value={money.format(report.totalEstimatedFeeSen)}
          hint="Customer-absorbed, for reference"
        />
      </div>

      {report.byPurpose.wash_pack.count > 0 || report.byPurpose.wash_ticket.count > 0 ? (
        <div style={purposeBreakdownStyle}>
          <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
            Breakdown:
          </span>
          {report.byPurpose.wash_pack.count > 0 ? (
            <span style={{ fontSize: 'var(--text-meta)' }}>
              {report.byPurpose.wash_pack.count} wash packs ·{' '}
              <strong>{money.format(report.byPurpose.wash_pack.totalSen)}</strong>
            </span>
          ) : null}
          {report.byPurpose.wash_ticket.count > 0 ? (
            <span style={{ fontSize: 'var(--text-meta)' }}>
              {report.byPurpose.wash_ticket.count} wash tickets ·{' '}
              <strong>{money.format(report.byPurpose.wash_ticket.totalSen)}</strong>
            </span>
          ) : null}
        </div>
      ) : null}

      {report.mismatches.length > 0 ? <MismatchList mismatches={report.mismatches} /> : null}
    </article>
  );
}

function CleanPill({
  clean,
  mismatchCount,
}: {
  readonly clean: boolean;
  readonly mismatchCount: number;
}) {
  const palette = clean
    ? { bg: 'rgba(13,122,79,0.10)', fg: 'var(--color-primary)' }
    : { bg: 'rgba(245,158,11,0.14)', fg: '#b45309' };
  return (
    <span
      style={{
        padding: '4px 12px',
        borderRadius: 'var(--radius-pill)',
        background: palette.bg,
        color: palette.fg,
        fontSize: 'var(--text-meta)',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {clean ? '✓ Clean' : `⚠ ${mismatchCount} mismatch${mismatchCount === 1 ? '' : 'es'}`}
    </span>
  );
}

function MismatchList({ mismatches }: { readonly mismatches: readonly ReconciliationMismatch[] }) {
  return (
    <details style={mismatchBoxStyle}>
      <summary
        style={{
          cursor: 'pointer',
          fontSize: 'var(--text-meta)',
          fontWeight: 500,
          color: '#b45309',
        }}
      >
        View {mismatches.length} mismatch{mismatches.length === 1 ? '' : 'es'}
      </summary>
      <ul
        style={{
          listStyle: 'none',
          margin: 'var(--space-3) 0 0 0',
          padding: 0,
          display: 'grid',
          gap: 'var(--space-2)',
        }}
      >
        {mismatches.map((m, i) => (
          <li key={`${m.intentId ?? 'orphan'}-${i}`} style={mismatchItemStyle}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 'var(--space-3)',
                alignItems: 'baseline',
                flexWrap: 'wrap',
              }}
            >
              <strong style={{ fontSize: 'var(--text-meta)', color: '#991b1b' }}>
                {mismatchReasonLabel(m.reason)}
              </strong>
              <span
                style={{
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                  fontFamily: 'ui-monospace, monospace',
                }}
              >
                {m.tenantId}
                {m.intentId ? ` · ${m.intentId}` : ''}
              </span>
            </div>
            <p
              style={{
                margin: 'var(--space-1) 0 0 0',
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface)',
              }}
            >
              {m.detail}
            </p>
            {m.expectedSen !== undefined || m.actualSen !== undefined ? (
              <p
                style={{
                  margin: 'var(--space-1) 0 0 0',
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                }}
              >
                {m.expectedSen !== undefined ? `Expected ${money.format(m.expectedSen)}` : ''}
                {m.expectedSen !== undefined && m.actualSen !== undefined ? ' · ' : ''}
                {m.actualSen !== undefined ? `Actual ${money.format(m.actualSen)}` : ''}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </details>
  );
}

function mismatchReasonLabel(reason: ReconciliationMismatchReason): string {
  switch (reason) {
    case 'paid_intent_no_ledger':
      return 'Paid intent · no ledger entry';
    case 'amount_mismatch':
      return 'Amount mismatch · intent vs ledger';
    case 'orphan_ledger':
      return 'Orphan ledger entry · no matching intent';
    case 'wash_pack_not_issued':
      return 'Wash pack paid · subscription not issued';
    case 'wash_ticket_not_settled':
      return 'Wash ticket paid · ticket.payment not set';
  }
}

// ── Small primitives ───────────────────────────────────────────────────

function Stat({
  label,
  value,
  hint,
}: {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
}) {
  return (
    <div>
      <p
        style={{
          margin: 0,
          fontSize: '0.6875rem',
          color: 'var(--color-on-surface-muted)',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: 'var(--space-1) 0 0 0',
          fontSize: 'var(--text-body)',
          fontWeight: 500,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </p>
      {hint ? (
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function InlineNote({
  children,
  tone = 'muted',
}: {
  readonly children: React.ReactNode;
  readonly tone?: 'muted' | 'error';
}) {
  return (
    <p
      role={tone === 'error' ? 'alert' : undefined}
      style={{
        margin: 0,
        padding: '12px 14px',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--text-meta)',
        background: tone === 'error' ? 'rgba(239,68,68,0.06)' : 'var(--tint-subtle)',
        border: `1px solid ${tone === 'error' ? 'rgba(239,68,68,0.20)' : 'var(--color-border)'}`,
        color: tone === 'error' ? '#991b1b' : 'var(--color-on-surface-muted)',
      }}
    >
      {children}
    </p>
  );
}

function SkeletonRow() {
  return (
    <div
      style={{
        height: 120,
        borderRadius: 12,
        background: 'var(--tint-subtle)',
        animation: 'kilatPulse 1.2s ease-in-out infinite',
      }}
    >
      <style>{`@keyframes kilatPulse { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }`}</style>
    </div>
  );
}

function Screen({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body?: string;
  readonly cta?: { label: string; to: string };
}) {
  return (
    <main style={{ ...pageStyle, display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center', maxWidth: 480, padding: 'var(--space-6)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h1>
        {body ? (
          <p
            style={{
              margin: 'var(--space-3) 0 0 0',
              color: 'var(--color-on-surface-muted)',
              lineHeight: 1.5,
            }}
          >
            {body}
          </p>
        ) : null}
        {cta ? (
          <Link
            to={cta.to}
            style={{
              display: 'inline-block',
              marginTop: 'var(--space-4)',
              padding: '12px 22px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-primary)',
              color: 'var(--color-primary-fg)',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            {cta.label}
          </Link>
        ) : null}
      </div>
    </main>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

function isSuperAdminForBrand(claims: Record<string, unknown> | null, brandId: string): boolean {
  if (!claims) return false;
  const sa = (claims as { superadmin?: Record<string, unknown> }).superadmin;
  if (!sa || typeof sa !== 'object') return false;
  return sa[brandId] === true;
}

function formatTimeFromTs(ts: { toMillis?: () => number; seconds?: number } | undefined): string {
  if (!ts) return '—';
  const ms =
    typeof ts.toMillis === 'function'
      ? ts.toMillis()
      : typeof ts.seconds === 'number'
        ? ts.seconds * 1000
        : 0;
  if (!ms) return '—';
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function extractFriendlyError(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const m = String((err as { message: string }).message);
    if (m.includes('permission-denied')) {
      return "You don't have permission to run reconciliation for this brand.";
    }
    if (m.includes('invalid-argument')) {
      return 'The period range was invalid.';
    }
    return m;
  }
  return 'Something went wrong. Try again.';
}

// ── Styles ─────────────────────────────────────────────────────────────

const headerStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  borderBottom: '1px solid var(--color-border)',
};

const contentStyle: React.CSSProperties = {
  maxWidth: PAGE_FRAME_MAX,
  margin: '0 auto',
  padding: 'var(--space-5)',
};

const reportCardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  boxShadow: 'var(--shadow-1)',
  display: 'grid',
  gap: 'var(--space-3)',
};

const statGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))',
  gap: 'var(--space-3) var(--space-4)',
};

const purposeBreakdownStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--space-3)',
  alignItems: 'baseline',
  paddingTop: 'var(--space-2)',
  borderTop: '1px dashed var(--color-border)',
};

const sectionHeadingStyle: React.CSSProperties = {
  margin: 'var(--space-3) 0 var(--space-2) 0',
  fontSize: 'var(--text-h2)',
  fontWeight: 600,
};

const uploadCardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-4)',
  boxShadow: 'var(--shadow-1)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
};

const dismissBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  color: 'inherit',
  textDecoration: 'underline',
  cursor: 'pointer',
};

const mismatchBoxStyle: React.CSSProperties = {
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  background: 'rgba(245,158,11,0.06)',
  border: '1px solid rgba(245,158,11,0.22)',
};

const mismatchItemStyle: React.CSSProperties = {
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-sm)',
  background: 'rgba(255,255,255,0.6)',
  border: '1px solid rgba(245,158,11,0.18)',
};
